"use client";

import { useCatalogFilters } from "@/stores/catalog-filters";
import type {
  PlainCatalogStrings,
  CategorySlug,
  ColorTag,
} from "@/lib/i18n/catalog-strings";

const CATEGORIES: CategorySlug[] = [
  "rose",
  "garden-spray-rose",
  "hydrangea",
  "specialty",
  "filler-green",
];

const LENGTHS = [50, 60, 70, 80] as const;

const COLORS: ColorTag[] = [
  "red",
  "pink",
  "hot-pink",
  "peach",
  "white",
  "cream",
  "blue",
  "green",
  "purple",
  "orange",
  "yellow",
  "bicolor",
  "lavender",
  "brown",
  "sand",
];

const COLOR_HEX: Record<ColorTag, string> = {
  red: "#DC143C",
  pink: "#FFB6C1",
  "hot-pink": "#FF69B4",
  peach: "#FFA07A",
  white: "#FAFAFA",
  cream: "#FFF8DC",
  blue: "#4169E1",
  green: "#9CAF88",
  purple: "#9370DB",
  orange: "#FFA500",
  yellow: "#FFD700",
  bicolor: "#FFD700",
  lavender: "#E6E6FA",
  brown: "#8B4513",
  sand: "#D2B48C",
};

export function FilterPanel({ strings }: { strings: PlainCatalogStrings }) {
  // Step 6.7.2 / AC165: this panel and CategoryTabs share the same
  // store. Toggle from either side updates both UIs.
  const categories = useCatalogFilters((s) => s.categories);
  const lengths = useCatalogFilters((s) => s.lengths);
  const colors = useCatalogFilters((s) => s.colors);
  const toggleCategory = useCatalogFilters((s) => s.toggleCategory);
  const toggleLength = useCatalogFilters((s) => s.toggleLength);
  const toggleColor = useCatalogFilters((s) => s.toggleColor);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-zinc-900">
          {strings.filterCategory}
        </h3>
        <ul className="space-y-1.5">
          {CATEGORIES.map((cat) => (
            <li key={cat}>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
                <input
                  type="checkbox"
                  data-testid={`filter-category-${cat}`}
                  checked={categories.includes(cat)}
                  onChange={() => toggleCategory(cat)}
                  className="h-4 w-4 cursor-pointer rounded border-zinc-300 text-brand-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-teal"
                />
                {strings.category[cat]}
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-zinc-900">
          {strings.filterLength}
        </h3>
        <ul className="space-y-1.5">
          {LENGTHS.map((l) => (
            <li key={l}>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
                <input
                  type="checkbox"
                  data-testid={`filter-length-${l}`}
                  checked={lengths.includes(String(l))}
                  onChange={() => toggleLength(String(l))}
                  className="h-4 w-4 cursor-pointer rounded border-zinc-300 text-brand-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-teal"
                />
                {l === 80 ? strings.length80Label : l} {strings.lengthSuffixCm}
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-zinc-900">
          {strings.filterColor}
        </h3>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((c) => {
            const isActive = colors.includes(c);
            return (
              <button
                key={c}
                type="button"
                data-testid={`filter-color-${c}`}
                onClick={() => toggleColor(c)}
                aria-label={strings.color[c]}
                aria-pressed={isActive}
                className={
                  isActive
                    ? "h-7 w-7 cursor-pointer rounded-full border-2 border-brand-teal ring-2 ring-brand-teal/30"
                    : "h-7 w-7 cursor-pointer rounded-full border border-brand-border hover:border-brand-text-muted"
                }
                style={
                  c === "bicolor"
                    ? { background: "linear-gradient(135deg, #FFD700 50%, #DC143C 50%)" }
                    : { backgroundColor: COLOR_HEX[c] }
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
